import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { OccasionGeniusEvent } from 'src/app/core/types/models/occasion-genius-event.model';
import { OccasionGeniusResponse } from 'src/app/core/types/models/occasion-genius-response.model';
import { IOccasionGeniusResponse } from 'src/app/core/types/interfaces/occasion-genius-response.interface';
import { IOccasionGeniusRecurringEvent } from 'src/app/core/types/interfaces/occasion-genius-event.interface';

import { BreakpointService } from 'src/app/core/services/breakpoint.service';
import { OccasionGeniusService } from 'src/app/core/services/occasion-genius.service';
import { FavoritesProviderService } from 'src/app/core/services/favorites-provider.service';
import { ThingsToDoService } from 'src/app/modules/country-info/services/things-to-do.service';

import { OccasionGeniusCardComponent } from '../occasion-genius-card/occasion-genius-card.component';
import { OccasionGeniusListMobileComponent } from 'src/app/modules/countries/components/mobile/occasion-genius-list-mobile/occasion-genius-list-mobile.component';

import moment from 'moment';

type OccasionGeniusType = 'preview' | 'filter';

export interface IFavouriteValue {
  favoriteId: string;
  data: any;
}

@Component({
  selector: 'app-occasion-genius',
  templateUrl: './occasion-genius.component.html',
  styleUrls: ['./occasion-genius.component.scss'],
  imports: [
    TranslateModule,
    OccasionGeniusCardComponent,
    OccasionGeniusListMobileComponent,
  ],
  providers: [OccasionGeniusService, ThingsToDoService],
})
export class OccasionGeniusComponent implements OnInit, OnChanges {
  @Input() title: 'Events and Occasions' | 'Event Calendar' =
    'Events and Occasions';
  @Input({ required: true }) cityId: string | number;
  @Input({ required: true }) cityName: string;
  @Input({ required: true }) countryName: string;
  @Input() isFetching: boolean = false;
  @Input() search: string = '';
  @Input() type: OccasionGeniusType = 'preview';
  @Input() occasionGeniusResponse: OccasionGeniusResponse =
    new OccasionGeniusResponse();
  @Input() eventsSearchedAndSorted: Array<
    OccasionGeniusEvent | IOccasionGeniusRecurringEvent
  > = [];
  @Output() onHide: EventEmitter<boolean> = new EventEmitter<boolean>();

  visibleEvents: Array<OccasionGeniusEvent | IOccasionGeniusRecurringEvent> =
    [];

  fetchingEvents: number[] = new Array(6);
  private pageSize: number = 6;
  private currentPage: number = 1;

  getCardEvent(event: any): OccasionGeniusEvent[] {
    return event?.events?.length ? event.events : [event];
  }

  private occasionGeniusParams = {
    from: moment().add(1, 'days'),
    to: moment().add(1, 'days').add(1, 'year'),
    currentPage: 1,
  };

  constructor(
    private occasionGeniusService: OccasionGeniusService,
    private favoritesProviderService: FavoritesProviderService,
    protected breakpointService: BreakpointService,
    private cdr: ChangeDetectorRef,
  ) {}

  get searchInCityPeriod(): string {
    if (!this.cityName) return '';
    const startYear = this.occasionGeniusParams.from.get('year').toString();
    const endYear = this.occasionGeniusParams.to.get('year').toString();
    const yearPeriod =
      startYear === endYear ? startYear : `${startYear}-${endYear}`;
    return `${this.cityName}, ${yearPeriod}`;
  }

  ngOnInit() {
    this.updateVisibleEvents();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.eventsSearchedAndSorted?.currentValue?.length) {
      this.updateVisibleEvents();
    }
    if (changes?.search) {
      this.updateVisibleEvents();
    }
  }

  groupByRecurringEventUuid(
    events: OccasionGeniusEvent[],
    search: string,
  ): {
    recurringEventUuid: string;
    events: OccasionGeniusEvent[];
  }[] {
    const filteredEvents = events.filter(
      (e) =>
        e.name.toLowerCase().includes(search.toLowerCase()) ||
        moment(e.start_date, 'YYYY-MM-DD HH:mm:ss')
          .format('MMMM Do YYYY, h:mmA')
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        e.flags?.toLowerCase().includes(search.toLowerCase()),
    );

    const groupedEvents = filteredEvents.reduce((acc, event) => {
      const key = event.recurring_event_uuid || event.uuid;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(event);
      return acc;
    }, {});

    const result = Object.keys(groupedEvents).map((key) => ({
      recurringEventUuid: key,
      events: groupedEvents[key].sort((a, b) => {
        return (
          new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
        );
      }),
    }));

    this.onHide.emit(!result.length);

    return result;
  }

  updateVisibleEvents() {
    if (!this.occasionGeniusResponse?.eventsSearchedAndSorted?.length) return;
    const end = this.currentPage * this.pageSize;
    this.visibleEvents =
      this.occasionGeniusResponse.eventsSearchedAndSortedCloned.slice(0, end);
  }

  onShowMore() {
    this.currentPage++;
    this.updateVisibleEvents();

    const maxPages = Math.ceil(
      this.occasionGeniusResponse.eventsSearchedAndSorted.length /
        this.pageSize,
    );
    if (this.currentPage === maxPages - 1) {
      this.loadOccasionGenius();
    }
  }

  private loadOccasionGenius(): void {
    this.occasionGeniusService
      .getOccasionGeniusAll(this.cityId, {
        from: this.occasionGeniusParams.from.unix(),
        to: this.occasionGeniusParams.to.unix(),
        offset: this.occasionGeniusParams.currentPage * 50,
      })
      .subscribe((response: IOccasionGeniusResponse) => {
        this.occasionGeniusParams.currentPage += 1;
        const occasionGeniusResponse = new OccasionGeniusResponse(response);
        this.occasionGeniusResponse.eventsSearchedAndSortedCloned =
          this.occasionGeniusResponse.eventsSearchedAndSortedCloned.concat(
            occasionGeniusResponse.eventsSearchedAndSortedCloned,
          );
        // this.events = this.events.concat(
        //   occasionGeniusResponse.eventsSearchedAndSorted,
        // );
      });
  }
}
